import { Koi } from '@ksyos/koi';
import type { Section } from '@common/formEngine/formEngine.types';
import type { DeepPickPath } from '@common/utils/deepPickUtils';
import type { NullablePrimitives } from '@common/utils/utilityTypes';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import { getTodayOrBeforeDateSchema } from '@common/validation/utils/dateSchemas';

export enum VoorlopigeIndicatie {
    chronische_migraine = 'Chronische migraine',
    medicatieafhankelijke_hoofdpijn = 'Medicatieafhankelijke hoofdpijn',
    migraine = 'Migraine',
    spanningshoofdpijn = 'Spanningshoofdpijn',
    vermoeden_van_migraine = 'Vermoeden van migraine',
    vermoeden_van_spanningshoofdpijn = 'Vermoeden van spanningshoofdpijn',
}

export const voorlopigeIndicatieOptions: VoorlopigeIndicatie[] = [
    VoorlopigeIndicatie.migraine,
    VoorlopigeIndicatie.chronische_migraine,
    VoorlopigeIndicatie.spanningshoofdpijn,
    VoorlopigeIndicatie.vermoeden_van_migraine,
    VoorlopigeIndicatie.vermoeden_van_spanningshoofdpijn,
    VoorlopigeIndicatie.medicatieafhankelijke_hoofdpijn,
];

export type HeadacheExaminationData = NullablePrimitives<{
    assets: ProcessActivityAsset[];
    hoofdpijn_aantal_dagen: number;
    hoofdpijn_aantal_dagen_migraine: number;
    hoofdpijn_aantal_dagen_migraine_met_aura: number;
    hoofdpijndagboek_eind: string | Date;
    hoofdpijndagboek_start: string | Date;
    medicatiegebruik_aantal_dagen: number;
    medicatiegebruik_aantal_dagen_paracetamol_nsaids: number;
    medicatiegebruik_aantal_dagen_triptanen: number;
    voorlopige_indicatie: VoorlopigeIndicatie;
}>;

export type HeadacheExaminationKey = DeepPickPath<HeadacheExaminationData>;

export const headacheExaminationSections: Section<HeadacheExaminationData>[] = [
    {
        fields: [
            {
                isRequired: true,
                key: 'assets',
                label: 'Rapport hoofdpijndagboek',
                type: 'assets',
            },
            {
                draftSchema: Koi.koi().date().allow(null),
                finalSchema: getTodayOrBeforeDateSchema().allow(null),
                key: 'hoofdpijndagboek_start',
                label: 'Start hoofdpijndagboek',
                maxValue: new Date(),
                type: 'dateInput',
            },
            {
                draftSchema: Koi.koi().date().allow(null),
                finalSchema: getTodayOrBeforeDateSchema().allow(null),
                key: 'hoofdpijndagboek_eind',
                label: 'Eind hoofdpijndagboek',
                maxValue: new Date(),
                type: 'dateInput',
            },
            {
                append: 'dagen',
                key: 'hoofdpijn_aantal_dagen',
                label: 'Aantal dagen hoofdpijn',
                maxValue: 31,
                minValue: 0,
                type: 'number',
            },
            {
                append: 'dagen',
                key: 'hoofdpijn_aantal_dagen_migraine',
                label: 'Waarvan migraine',
                maxValue: 31,
                minValue: 0,
                type: 'number',
            },
            {
                append: 'dagen',
                key: 'hoofdpijn_aantal_dagen_migraine_met_aura',
                label: 'Waarvan migraine met aura',
                maxValue: 31,
                minValue: 0,
                type: 'number',
            },
            {
                append: 'dagen',
                key: 'medicatiegebruik_aantal_dagen',
                label: 'Aantal dagen medicatiegebruik',
                maxValue: 31,
                minValue: 0,
                type: 'number',
            },
            {
                append: 'dagen',
                key: 'medicatiegebruik_aantal_dagen_paracetamol_nsaids',
                label: `Waarvan paracetamol / NSAID's`,
                maxValue: 31,
                minValue: 0,
                type: 'number',
            },
            {
                append: 'dagen',
                key: 'medicatiegebruik_aantal_dagen_triptanen',
                label: 'Waarvan triptanen',
                maxValue: 31,
                minValue: 0,
                type: 'number',
            },
            {
                key: 'voorlopige_indicatie',
                label: 'Voorlopige indicatie',
                type: 'radioGroup',
                valueOptions: voorlopigeIndicatieOptions,
            },
        ],
    },
];
